/**
 * Pure logic for the rider's cancel flow (components/CancelReasonModal.js):
 * reason first, then a follow-up question for that reason that offers a fix
 * before the rider confirms. Kept free of React so it runs under node --test
 * (cancelFlow.test.js).
 */

/** Every reason key, in the order shown. Values saved to ride.cancelReason. */
export const CANCEL_REASONS = [
  'wait_too_long',
  'wrong_pickup',
  'changed_plans',
  'found_other',
  'captain_issue',
  'other',
];

/** The free-cancel window after an Anna accepts (matches the server ladder). */
export const CANCEL_GRACE_SEC = 120;

/** Max length of the "Something else" note (fits the 200-char cancelReason rule). */
export const CANCEL_NOTE_MAX = 160;

/** 'searching' | 'assigned' | 'arrived', from the ride status. */
export const cancelStage = (status) => {
  if (status === 'arrived') return 'arrived';
  if (status === 'driver_assigned') return 'assigned';
  return 'searching';
};

/** Reasons that make sense at this stage: no Anna yet means no "Issue with the Anna". */
export const cancelReasonsFor = (stage) => (
  stage === 'searching' ? CANCEL_REASONS.filter((k) => k !== 'captain_issue') : CANCEL_REASONS
);

/**
 * What cancelling costs right now, mirroring the server's fair ladder:
 * 'none' (no Anna yet), 'grace' (within 2 min of accept, free), 'courtesy'
 * (first late cancel, free with a warning), 'fee' (₹10 on the next ride).
 * Arrived is never in grace: the Anna has already come.
 */
export const cancelPolicy = ({ stage, acceptedMs, now, lateCount }) => {
  if (stage === 'searching') return { kind: 'none', graceSecLeft: 0 };
  const elapsedSec = acceptedMs > 0 ? Math.floor((now - acceptedMs) / 1000) : Infinity;
  const graceSecLeft = Math.max(0, CANCEL_GRACE_SEC - elapsedSec);
  if (stage === 'assigned' && graceSecLeft > 0) return { kind: 'grace', graceSecLeft };
  return { kind: Number(lateCount) > 0 ? 'fee' : 'courtesy', graceSecLeft: 0 };
};

/** "1:05" for the grace countdown. */
export const formatGrace = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

/**
 * The follow-up for a reason: which copy to show and which fix to offer
 * instead of cancelling. `can` says which fixes are possible right now
 * ({ boost, call, chat, support, rebook }); an unavailable fix is dropped,
 * never shown disabled.
 *   kind  - copy group in translations (cancel.fu.*)
 *   action - 'boost' | 'call' | 'chat' | 'rebook' | 'support' | null
 *   note  - show the optional feedback box
 */
export const cancelFollowUp = (reason, stage, can = {}) => {
  const ride = stage !== 'searching';
  const pick = (action) => (action && can[action] ? action : null);
  switch (reason) {
    case 'wait_too_long':
      if (!ride) return { kind: 'waitSearching', action: pick('boost'), note: false };
      return { kind: stage === 'arrived' ? 'waitArrived' : 'waitAssigned', action: pick('call'), note: false };
    case 'wrong_pickup':
      return ride
        ? { kind: 'pickupRide', action: pick('chat'), note: false }
        : { kind: 'pickupSearching', action: pick('rebook'), note: false };
    case 'captain_issue':
      return { kind: 'issue', action: pick('support'), note: false };
    case 'found_other':
      return { kind: 'foundOther', action: null, note: false };
    case 'other':
      return { kind: 'plain', action: null, note: true };
    case 'changed_plans':
    default:
      return { kind: 'plain', action: null, note: false };
  }
};

/**
 * The string saved as ride.cancelReason (Firestore rule: a string of at most
 * 200 chars). Only "Something else" carries the rider's note, as
 * "other: <note>", so the admin Rides list, the ride timeline and the
 * Telegram alert show it with no server change. Line breaks are flattened.
 */
export const cancelReasonValue = (reason, note) => {
  const key = CANCEL_REASONS.includes(reason) ? reason : 'other';
  if (key !== 'other') return key;
  const clean = String(note || '').replace(/\s+/g, ' ').trim().slice(0, CANCEL_NOTE_MAX);
  return clean ? `other: ${clean}`.slice(0, 200) : 'other';
};
